
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatsResult } from '@/hooks/use-studio-stats';
import { Users, Calendar, Scissors, DollarSign } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

interface StudioOverviewStatsProps {
  stats: StatsResult;
  isLoading: boolean;
}

const StudioOverviewStats: React.FC<StudioOverviewStatsProps> = ({ stats, isLoading }) => {
  // Format data for charts
  const bookingsChartData = stats.bookingsByDay.map(day => ({
    date: day.day,
    Buchungen: day.count,
    formattedDate: format(parseISO(day.day), 'dd.MM', { locale: de })
  }));
  
  const weekdayData = stats.weekdayDistribution.map(day => ({
    day: day.day,
    Buchungen: day.count
  }));
  
  // Calculate utilization
  const totalAppointments = stats.appointments;
  const maxCapacity = 100; // Just for visualization
  const utilization = Math.min(100, Math.floor((totalAppointments / maxCapacity) * 100));
  
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Kundenzahl
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.customers}</div>
            <p className="text-xs text-muted-foreground">
              Aktive Kunden im Zeitraum
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Buchungen
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.appointments}</div>
            <p className="text-xs text-muted-foreground">
              Gesamte Termine im Zeitraum
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Umsatz
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.revenue.toLocaleString()} €</div>
            <p className="text-xs text-muted-foreground">
              Gesamter Umsatz im Zeitraum
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Leistungen
            </CardTitle>
            <Scissors className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.services}</div>
            <p className="text-xs text-muted-foreground">
              Aktive Leistungsarten
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Booking Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Buchungsverlauf</CardTitle>
          <CardDescription>
            Anzahl der Buchungen über den ausgewählten Zeitraum
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : bookingsChartData.length === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten für diesen Zeitraum verfügbar</p>
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bookingsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="formattedDate" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value}
                    interval={Math.floor(bookingsChartData.length / 15)}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`${value} Buchungen`, 'Anzahl']}
                    labelFormatter={(label) => `Datum: ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="Buchungen" fill="#2C6E49" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Weekday Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Verteilung nach Wochentagen</CardTitle>
          <CardDescription>
            Anzahl der Buchungen pro Wochentag
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : weekdayData.length === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekdayData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`${value} Buchungen`, 'Anzahl']}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Bar dataKey="Buchungen" fill="#2C6E49" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudioOverviewStats;
